import { isSupportSession } from "@/utils/supportSession";
import { isTeacher } from "./role-utils";
import { getDisplayName, type User } from "@/store/useAuthStore";

interface PendoPayload {
  visitor: {
    id: string;
    email?: string;
    full_name?: string;
    role?: string;
  };
  account?: {
    id: string;
    name?: string;
  };
}

interface PendoAgent {
  initialize: (opts: PendoPayload) => void;
  identify?: (opts: PendoPayload) => void;
  _q?: unknown[];
  _initialized?: boolean;
}

declare global {
  interface Window {
    pendo?: PendoAgent;
  }
}

function isEnabled(): boolean {
  return (
    import.meta.env.VITE_PENDO_ENABLED === "true" &&
    !!import.meta.env.VITE_PENDO_API_KEY
  );
}

function loadPendoAgent(apiKey: string) {
  if (window.pendo) return;

  const queueStub = { _q: [] as unknown[] } as PendoAgent & {
    _q: unknown[];
    [method: string]: unknown;
  };
  const methods = [
    "initialize",
    "identify",
    "updateOptions",
    "pageLoad",
    "track",
  ];
  for (const m of methods) {
    queueStub[m] = function (...args: unknown[]) {
      queueStub._q.push([m, ...args]);
    };
  }
  window.pendo = queueStub as PendoAgent;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
}

export const setPendoUser = (user: User) => {
  // A support session authenticates as the target, so identifying here
  // attributes the operator's whole session to that user. Guarded inside
  // the helper rather than at its call sites: there are four, and the one
  // that matters most is initialize() reading a cached user on reload --
  // which is why the marker is seeded from localStorage at module load.
  if (isSupportSession()) return;
  if (!isEnabled() || !isTeacher(user.role)) return;

  loadPendoAgent(import.meta.env.VITE_PENDO_API_KEY);
  if (!window.pendo) return;

  const payload: PendoPayload = {
    visitor: {
      id: String(user.id),
      email: user.email,
      full_name: getDisplayName(user),
      role: user.role,
    },
    // account: populated once org/school identity ships
  };

  if (!window.pendo._initialized) {
    window.pendo.initialize(payload);
    window.pendo._initialized = true;
  } else {
    window.pendo.identify?.(payload);
  }
};
